import React, { useState } from 'react';
import { getApiUrl } from '../config';

type Result = 'WIN' | 'DRAW' | 'LOSS';

interface Entry {
  date: string;
  result: Result;
  margin?: number;
  goals: number;
  assists: number;
  saves: number;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const GREEN = '#00E676';
const RED = '#FF5252';
const YELLOW = '#FFD600';
const CARD_BG = '#23272A';
const CARD_BORDER = '#33383B';
const MUTED = '#B0BEC5';
const PANEL_BG = '#181A1B';

function todayISO(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

function fmtDDMM(iso: string): string {
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
}

const RESULT_META: Record<Result, { label: string; color: string }> = {
  WIN: { label: 'Gané', color: GREEN },
  DRAW: { label: 'Empaté', color: YELLOW },
  LOSS: { label: 'Perdí', color: RED },
};

const AddMatchModal: React.FC<Props> = ({ isOpen, onClose, onSaved }) => {
  const [date, setDate] = useState<string>(todayISO());
  const [result, setResult] = useState<Result>('WIN');
  const [margin, setMargin] = useState<number>(1);
  const [goals, setGoals] = useState<number>(0);
  const [assists, setAssists] = useState<number>(0);
  const [saves, setSaves] = useState<number>(0);
  const [statsOpen, setStatsOpen] = useState(false);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const needsMargin = result === 'WIN' || result === 'LOSS';

  const resetDraft = () => {
    setDate(todayISO());
    setResult('WIN');
    setMargin(1);
    setGoals(0);
    setAssists(0);
    setSaves(0);
    setStatsOpen(false);
  };

  const buildDraft = (): Entry => {
    const entry: Entry = {
      date,
      result,
      goals: Math.max(0, Math.round(goals)),
      assists: Math.max(0, Math.round(assists)),
      saves: Math.max(0, Math.round(saves)),
    };
    if (needsMargin) entry.margin = Math.max(0, Math.round(margin));
    return entry;
  };

  const addToList = () => {
    setError(null);
    setEntries((prev) => [...prev, buildDraft()]);
    resetDraft();
  };

  const removeEntry = (idx: number) => {
    setEntries((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleClose = () => {
    setEntries([]);
    resetDraft();
    setError(null);
    onClose();
  };

  const handleSave = async () => {
    setError(null);
    // Incluir el draft actual si el usuario no lo agregó a la lista todavía.
    const toSave: Entry[] = [...entries];
    if (entries.length === 0) {
      toSave.push(buildDraft());
    }
    if (toSave.length === 0) {
      setError('Cargá al menos un partido.');
      return;
    }
    try {
      setSaving(true);
      const token = localStorage.getItem('authToken');
      const body = toSave.length === 1 ? toSave[0] : { entries: toSave };
      const res = await fetch(getApiUrl('/api/users/me/match-log'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        let msg = 'No se pudo guardar. Probá de nuevo.';
        try {
          const data = await res.json();
          if (data?.error) msg = data.error;
        } catch {
          // noop
        }
        setError(msg);
        return;
      }
      handleClose();
      onSaved();
    } catch {
      setError('Error de conexión. Probá de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  const overlay: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '16px',
  };
  const modal: React.CSSProperties = {
    background: CARD_BG,
    border: `1px solid ${CARD_BORDER}`,
    borderRadius: '18px',
    padding: '20px',
    width: '100%',
    maxWidth: '440px',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
  };
  const label: React.CSSProperties = {
    fontSize: '12px',
    fontWeight: 600,
    color: MUTED,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    marginBottom: '6px',
    display: 'block',
  };
  const inputStyle: React.CSSProperties = {
    width: '100%',
    background: PANEL_BG,
    border: `1px solid ${CARD_BORDER}`,
    borderRadius: '10px',
    color: '#FFFFFF',
    padding: '10px 12px',
    fontSize: '15px',
    boxSizing: 'border-box',
  };

  return (
    <div style={overlay} onClick={handleClose}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, color: '#FFFFFF', fontSize: '18px', fontWeight: 700 }}>Agregar partido</h3>
          <button
            onClick={handleClose}
            style={{ background: 'none', border: 'none', color: MUTED, fontSize: '22px', cursor: 'pointer', lineHeight: 1 }}
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        {/* Fecha */}
        <div style={{ marginBottom: '16px' }}>
          <label style={label}>Fecha</label>
          <input type="date" value={date} max={todayISO()} onChange={(e) => setDate(e.target.value)} style={inputStyle} />
        </div>

        {/* Resultado */}
        <div style={{ marginBottom: '16px' }}>
          <label style={label}>¿Cómo te fue?</label>
          <div style={{ display: 'flex', gap: '8px' }}>
            {(Object.keys(RESULT_META) as Result[]).map((r) => {
              const active = result === r;
              const { label: rLabel, color } = RESULT_META[r];
              return (
                <button
                  key={r}
                  onClick={() => setResult(r)}
                  style={{
                    flex: 1,
                    padding: '12px 0',
                    borderRadius: '10px',
                    border: `1.5px solid ${color}`,
                    background: active ? color : 'transparent',
                    color: active ? '#181A1B' : color,
                    fontWeight: 700,
                    fontSize: '15px',
                    cursor: 'pointer',
                  }}
                >
                  {rLabel}
                </button>
              );
            })}
          </div>
        </div>

        {/* Margen */}
        {needsMargin && (
          <div style={{ marginBottom: '16px' }}>
            <label style={label}>¿Por cuántos goles?</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <button
                onClick={() => setMargin((m) => Math.max(0, m - 1))}
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '10px',
                  border: `1px solid ${CARD_BORDER}`,
                  background: PANEL_BG,
                  color: '#FFFFFF',
                  fontSize: '22px',
                  cursor: 'pointer',
                }}
                aria-label="Restar gol"
              >
                −
              </button>
              <input
                type="number"
                min={0}
                value={margin}
                onChange={(e) => setMargin(Math.max(0, Math.round(Number(e.target.value) || 0)))}
                style={{ ...inputStyle, textAlign: 'center', width: '70px', flex: 'none' }}
              />
              <button
                onClick={() => setMargin((m) => m + 1)}
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '10px',
                  border: `1px solid ${CARD_BORDER}`,
                  background: PANEL_BG,
                  color: '#FFFFFF',
                  fontSize: '22px',
                  cursor: 'pointer',
                }}
                aria-label="Sumar gol"
              >
                +
              </button>
            </div>
          </div>
        )}

        {/* Goles / Asistencias / Atajadas (opcional, colapsable) */}
        <div style={{ marginBottom: '16px' }}>
          {!statsOpen ? (
            <button
              onClick={() => setStatsOpen(true)}
              style={{
                background: 'none',
                border: 'none',
                color: MUTED,
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                padding: 0,
              }}
            >
              ➕ Agregar goles, asistencias, atajadas
            </button>
          ) : (
            <div
              style={{
                background: PANEL_BG,
                border: `1px solid ${CARD_BORDER}`,
                borderRadius: '12px',
                padding: '12px',
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {([
                  { lbl: 'Goles', val: goals, set: setGoals },
                  { lbl: 'Asistencias', val: assists, set: setAssists },
                  { lbl: 'Atajadas', val: saves, set: setSaves },
                ] as const).map(({ lbl, val, set }) => (
                  <div key={lbl} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ color: '#FFFFFF', fontSize: '14px', fontWeight: 600 }}>{lbl}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <button
                        onClick={() => set((v) => Math.max(0, v - 1))}
                        style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '8px',
                          border: `1px solid ${CARD_BORDER}`,
                          background: CARD_BG,
                          color: '#FFFFFF',
                          fontSize: '20px',
                          cursor: 'pointer',
                        }}
                        aria-label={`Restar ${lbl}`}
                      >
                        −
                      </button>
                      <input
                        type="number"
                        min={0}
                        value={val}
                        onChange={(e) => set(Math.max(0, Math.round(Number(e.target.value) || 0)))}
                        style={{ ...inputStyle, textAlign: 'center', width: '60px', flex: 'none', background: CARD_BG }}
                      />
                      <button
                        onClick={() => set((v) => v + 1)}
                        style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '8px',
                          border: `1px solid ${CARD_BORDER}`,
                          background: CARD_BG,
                          color: '#FFFFFF',
                          fontSize: '20px',
                          cursor: 'pointer',
                        }}
                        aria-label={`Sumar ${lbl}`}
                      >
                        +
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <button
          onClick={addToList}
          style={{
            width: '100%',
            padding: '10px',
            borderRadius: '10px',
            border: `1px solid ${GREEN}`,
            background: 'transparent',
            color: GREEN,
            fontWeight: 700,
            fontSize: '14px',
            cursor: 'pointer',
            marginBottom: '16px',
          }}
        >
          + Cargar otro
        </button>

        {/* Lista de partidos cargados */}
        {entries.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <label style={label}>Por guardar ({entries.length})</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {entries.map((e, i) => {
                const { label: rLabel, color } = RESULT_META[e.result];
                return (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      background: PANEL_BG,
                      border: `1px solid ${CARD_BORDER}`,
                      borderRadius: '10px',
                      padding: '8px 12px',
                    }}
                  >
                    <span style={{ color: '#FFFFFF', fontSize: '14px' }}>
                      <span style={{ color: MUTED }}>{fmtDDMM(e.date)}</span>{'  '}
                      <span style={{ color, fontWeight: 700 }}>{rLabel}</span>
                      {e.margin !== undefined && e.result !== 'DRAW' && (
                        <span style={{ color: MUTED }}>{` por ${e.margin}`}</span>
                      )}
                      {(e.goals > 0 || e.assists > 0 || e.saves > 0) && (
                        <span style={{ color: MUTED, marginLeft: '8px' }}>
                          {e.goals > 0 && `⚽${e.goals} `}
                          {e.assists > 0 && `➡️${e.assists} `}
                          {e.saves > 0 && `🧤${e.saves}`}
                        </span>
                      )}
                    </span>
                    <button
                      onClick={() => removeEntry(i)}
                      style={{ background: 'none', border: 'none', color: RED, fontSize: '18px', cursor: 'pointer', lineHeight: 1 }}
                      aria-label="Quitar partido"
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {error && <div style={{ color: RED, fontSize: '13px', marginBottom: '12px' }}>{error}</div>}

        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            width: '100%',
            padding: '14px',
            borderRadius: '12px',
            border: 'none',
            background: GREEN,
            color: '#181A1B',
            fontWeight: 800,
            fontSize: '16px',
            cursor: saving ? 'default' : 'pointer',
            opacity: saving ? 0.6 : 1,
          }}
        >
          {saving
            ? 'Guardando…'
            : entries.length > 1
            ? `Guardar ${entries.length} partidos`
            : 'Guardar partido'}
        </button>
      </div>
    </div>
  );
};

export default AddMatchModal;
